import type { DailyPulse, PulseStory } from "@altair/shared/types";

/**
 * Shape of the SSR hydration payload the news-renderer injects. Fields vary by
 * route: list → `pulses`; pulse/story → `pulse` + `allPulses` (+ `storySlug`).
 */
export interface NewsHydrationData {
  route?: "list" | "pulse" | "story" | "404";
  pulses?: DailyPulse[];
  pulse?: DailyPulse;
  focusStory?: PulseStory;
  // Only `date` is needed (prev/next nav); the renderer ships date-only stubs to
  // keep the detail-page hydration payload small.
  allPulses?: Pick<DailyPulse, "date">[];
  storySlug?: string;
}

let cached: NewsHydrationData | null | undefined;

/**
 * Read the hydration payload the news-renderer ships as a NON-executable JSON
 * script — `<script id="__news-data__" type="application/json">…</script>` (see
 * apps/news-renderer/src/shell.ts). The renderer no longer seeds
 * `window.__NEWS_DATA__` (CSP forbids inline executable scripts), so the client
 * MUST read the element — otherwise the news pages fall back to the bundled
 * `samplePulses` and show stale sample data instead of live DB content.
 *
 * Memoized per document load. Returns null when the element is absent: during
 * SSR (the shimmed `document` yields an empty textContent) and in dev/SPA
 * preview — so callers fall back to the bundled sample data.
 */
export function readNewsHydrationData(): NewsHydrationData | null {
  if (cached !== undefined) return cached;
  try {
    const raw =
      typeof document !== "undefined"
        ? document.getElementById("__news-data__")?.textContent
        : null;
    cached = raw ? (JSON.parse(raw) as NewsHydrationData) : null;
  } catch {
    cached = null;
  }
  return cached;
}
